import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import axios from "axios";
import CompanyAddress from "../../components/CompanyAddress";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";
import CountryProvinceSelector from "../../components/CountryProvinceSelector";
import logo from "../../images/logo.png";

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const DRIVER_LICENSE_TASK_ID = 22;
const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];

const validateFiles = (fileList) =>
  Array.from(fileList || []).filter((file) => allowedTypes.includes(file.type));

const DriverLicenseForm = () => {
  const navigate = useNavigate();
  const { id: paramId, dlId } = useParams();
  const isNew = !dlId || dlId === "new";

  const role = localStorage.getItem("userRole");
  const isAdmin = role === "1";
  const loggedInId = localStorage.getItem("userId");
  const userId = isAdmin && paramId ? paramId : loggedInId;

  const [form, setForm] = useState({
    dl_id: null,
    status: "",
    licenseNumber: "",
    licenseClass: [],
    licenseCondition: [],
    country: "",
    province: "",
    expiryDate: "",
    files: [],
    uploadedFiles: [],
  });
  const [countries, setCountries] = useState([]);
  const [classes, setClasses] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [isLocked, setIsLocked] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeletingFileId, setIsDeletingFileId] = useState(null);
  const [currentStatus, setCurrentStatus] = useState("not_completed");
  const fileInputRef = useRef(null);

  const fetchTaskStatus = useCallback(async () => {
    if (!userId) return;

    try {
      const res = await axios.get(`${API_BASE}/api/usertasks/status/${userId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const referenceStatus =
        dlId && !isNew
          ? res.data.status?.[`${DRIVER_LICENSE_TASK_ID}_${dlId}`] ??
            res.data.status?.[`driver_license_${dlId}`]
          : null;
      const baseStatus =
        res.data.status?.[String(DRIVER_LICENSE_TASK_ID)] ??
        res.data.status?.driver_license ??
        "not_completed";
      const status = referenceStatus ?? (!isNew ? baseStatus : "not_completed");

      setCurrentStatus(status);
      setIsLocked(status === "completed");
    } catch (err) {
      console.error("Failed to fetch driver license status", err);
    }
  }, [dlId, isNew, userId]);

  useEffect(() => {
    const loadCountries = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/dropdowns/countries`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        setCountries(res.data || []);
      } catch (err) {
        console.error("Failed to fetch countries", err);
      }
    };

    loadCountries();
  }, []);

  useEffect(() => {
    fetchTaskStatus();
  }, [fetchTaskStatus]);

  useEffect(() => {
    if (isNew) {
      setForm((prev) => ({
        ...prev,
        status: prev.status || "",
      }));
      return;
    }

    const fetchLicense = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/driverlicense/detail/${dlId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        const data = res.data;
        setForm({
          dl_id: data.dl_id,
          status: data.dl_status || "",
          licenseNumber: data.dl_license_no || "",
          licenseClass: data.dl_class || [],
          licenseCondition: data.dl_condition || [],
          country: data.dl_country || "",
          province: data.dl_province || "",
          expiryDate: data.dl_expdate ? data.dl_expdate.substring(0, 10) : "",
          files: [],
          uploadedFiles: data.files || [],
        });
      } catch (err) {
        console.error("Failed to fetch driver license detail", err);
        alert("Failed to load driver license.");
        navigate(`/driverlicense/${userId}`);
      }
    };

    fetchLicense();
  }, [dlId, isNew, navigate, userId]);

  useEffect(() => {
    if (form.country !== "Canada" || !form.province) {
      setClasses([]);
      setConditions([]);
      return;
    }

    const fetchProvinceMeta = async () => {
      try {
        const token = localStorage.getItem("token");
        const provinceRes = await axios.get(
          `${API_BASE}/api/dropdowns/province-by-name`,
          {
            params: {
              country: form.country,
              province: form.province,
            },
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const provinceId = provinceRes.data?.id;
        if (!provinceId) return;

        const [classesRes, conditionsRes] = await Promise.all([
          axios.get(`${API_BASE}/api/dropdowns/license-classes/${provinceId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(
            `${API_BASE}/api/dropdowns/license-conditions/${provinceId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          ),
        ]);

        setClasses(classesRes.data || []);
        setConditions(conditionsRes.data || []);
      } catch (err) {
        console.error("Failed to fetch license dropdown metadata", err);
        setClasses([]);
        setConditions([]);
      }
    };

    fetchProvinceMeta();
  }, [form.country, form.province]);

  const handleSelectFiles = (e) => {
    const selected = validateFiles(e.target.files);

    if (selected.length !== (e.target.files?.length || 0)) {
      alert("Only PDF, JPG, or PNG files are allowed.");
    }

    setForm((prev) => ({
      ...prev,
      files: [...prev.files, ...selected],
    }));

    e.target.value = "";
  };

  const removePendingFile = (fileToRemove) => {
    setForm((prev) => ({
      ...prev,
      files: prev.files.filter(
        (file) =>
          !(
            file.name === fileToRemove.name &&
            file.size === fileToRemove.size &&
            file.lastModified === fileToRemove.lastModified
          )
      ),
    }));
  };

  const handleDeleteUploadedFile = async (file) => {
    if (!window.confirm(`Delete uploaded file "${file.original_name}"?`)) return;

    try {
      setIsDeletingFileId(file.id);
      await axios.delete(`${API_BASE}/api/driverlicense/file/${file.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setForm((prev) => ({
        ...prev,
        uploadedFiles: prev.uploadedFiles.filter((f) => f.id !== file.id),
      }));
    } catch (err) {
      console.error("Failed to delete uploaded driver license file", err);
      alert("Delete failed");
    } finally {
      setIsDeletingFileId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.status) {
      alert("Please choose Primary License or Other License.");
      return;
    }

    if (!form.country || !form.province || !form.licenseNumber || !form.expiryDate) {
      alert("Please complete all required fields.");
      return;
    }

    if (!form.licenseClass.length) {
      alert("Please select at least one license class.");
      return;
    }

    if (isNew && form.files.length === 0 && form.uploadedFiles.length === 0) {
      alert("Please upload at least one license file.");
      return;
    }

    setIsSaving(true);

    try {
      const formData = new FormData();
      form.files.forEach((file) => formData.append("files", file));
      formData.append("userId", userId);
      formData.append("dl_id", form.dl_id || "");
      formData.append("status", form.status);
      formData.append("license_number", form.licenseNumber);
      formData.append("class", form.licenseClass.join(","));
      formData.append("condition", form.licenseCondition.join(","));
      formData.append("province", form.province);
      formData.append("country", form.country);
      formData.append("expiry_date", form.expiryDate);

      const res = await axios.post(
        `${API_BASE}/api/driverlicense/upload?userId=${userId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      alert("Driver license saved successfully!");
      const savedDlId = res.data?.dl_id || form.dl_id;

      if (isNew && savedDlId) {
        navigate(`/driverlicense/${userId}/detail/${savedDlId}`);
      } else {
        navigate(`/driverlicense/${userId}`);
      }
    } catch (err) {
      console.error("Driver license save failed", err);
      alert("Save failed");
    } finally {
      setIsSaving(false);
    }
  };

  const isDisabled = !isAdmin && isLocked;

  return (
    <>
      <div className="d-flex justify-content-between align-items-center pt-3">
        <span style={{ fontSize: "18px", fontWeight: "500" }}>
          Driver&apos;s License
        </span>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={() => navigate(`/driverlicense/${userId}`)}
          >
            View List
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate(-1)}
          >
            Return
          </button>
        </div>
      </div>

      <div className="form-wrapper my-4">
        <div className="row align-items-center mb-4">
          <div className="col-3 text-start">
            <img src={logo} alt="Company Logo" style={{ width: "150px" }} />
          </div>
          <div className="col-6 text-center">
            <h2 className="m-0">Driver&apos;s License</h2>
          </div>
          <CompanyAddress />
        </div>

        <form onSubmit={handleSubmit}>
          <div className="row mb-3">
            <div className="col-md-4">
              <label className="form-label">License Type *</label>
              <select
                className="form-select"
                value={form.status}
                disabled={!isNew || isDisabled}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, status: e.target.value }))
                }
              >
                <option value="">Select License Type</option>
                <option value="primary">Primary License</option>
                <option value="other">Other License</option>
              </select>
            </div>
          </div>

          <h5 className="mb-3">
            {form.status === "other" ? "Other License" : "Primary License"}
          </h5>

          <CountryProvinceSelector
            country={form.country}
            province={form.province}
            countries={countries}
            disabled={isDisabled}
            onChange={(field, value) =>
              setForm((prev) => ({ ...prev, [field]: value }))
            }
          />

          <div className="row mb-3">
            <div className="col-md-3">
              <label className="form-label">License Number *</label>
              <input
                type="text"
                className="form-control"
                value={form.licenseNumber}
                disabled={isDisabled}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, licenseNumber: e.target.value }))
                }
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">License Class *</label>
              {form.country === "Canada" ? (
                <Select
                  isMulti
                  value={form.licenseClass.map((c) => ({ value: c, label: c }))}
                  options={classes.map((c) => ({
                    value: c.class_code,
                    label: c.description
                      ? `${c.class_code} - ${c.description}`
                      : c.class_code,
                  }))}
                  isDisabled={isDisabled}
                  onChange={(selected) =>
                    setForm((prev) => ({
                      ...prev,
                      licenseClass: selected.map((s) => s.value),
                    }))
                  }
                />
              ) : (
                <input
                  type="text"
                  className="form-control"
                  value={form.licenseClass.join(",")}
                  disabled={isDisabled}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      licenseClass: e.target.value
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean),
                    }))
                  }
                />
              )}
            </div>
            <div className="col-md-3">
              <label className="form-label">Condition</label>
              {form.country === "Canada" ? (
                <Select
                  isMulti
                  value={form.licenseCondition.map((c) => ({
                    value: c,
                    label: c,
                  }))}
                  options={conditions.map((c) => ({
                    value: c.condition_code,
                    label: c.description
                      ? `${c.condition_code} - ${c.description}`
                      : c.condition_code,
                  }))}
                  isDisabled={isDisabled}
                  onChange={(selected) =>
                    setForm((prev) => ({
                      ...prev,
                      licenseCondition: selected.map((s) => s.value),
                    }))
                  }
                />
              ) : (
                <input
                  type="text"
                  className="form-control"
                  value={form.licenseCondition.join(",")}
                  disabled={isDisabled}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      licenseCondition: e.target.value
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean),
                    }))
                  }
                />
              )}
            </div>
            <div className="col-md-3">
              <label className="form-label">Expiry Date *</label>
              <input
                type="date"
                className="form-control"
                value={form.expiryDate}
                disabled={isDisabled}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, expiryDate: e.target.value }))
                }
              />
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label">
              Select License File (PDF / JPG / PNG)
              {isNew ? " *" : ""}
            </label>
            <input
              type="file"
              className="form-control"
              multiple
              accept=".pdf,.jpg,.jpeg,.png"
              ref={fileInputRef}
              disabled={isDisabled}
              onChange={handleSelectFiles}
            />
          </div>

          {form.files.length > 0 && (
            <div className="mt-4 p-3 bg-light rounded border">
              <div className="text-muted mb-2">Files Ready To Save</div>
              {form.files.map((file, idx) => (
                <div
                  key={`${file.name}_${file.size}_${file.lastModified}_${idx}`}
                  className="d-flex justify-content-between align-items-center mb-2"
                >
                  <span>{file.name}</span>
                  {!isDisabled && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => removePendingFile(file)}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {form.uploadedFiles.length > 0 && (
            <div className="mt-4 p-3 bg-light rounded border">
              <div className="text-muted mb-2">Uploaded Files</div>
              {form.uploadedFiles.map((file) => (
                <div
                  key={file.id}
                  className="d-flex justify-content-between align-items-center mb-2"
                >
                  <a
                    href={`${API_BASE}/uploads/driverlicenses/${file.path}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {file.original_name}
                  </a>
                  {!isDisabled && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      disabled={isDeletingFileId === file.id}
                      onClick={() => handleDeleteUploadedFile(file)}
                    >
                      {isDeletingFileId === file.id ? "Deleting..." : "Delete"}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="d-flex justify-content-end">
            <button
              type="submit"
              className="btn btn-primary mt-3"
              disabled={isDisabled || isSaving}
            >
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </form>

        {!isNew && (
          <>
            <hr className="my-4" />
            {currentStatus === "not_completed" && (
              <SignatureManager
                taskName="driver_license"
                taskId={DRIVER_LICENSE_TASK_ID}
                referenceId={dlId}
                userId={userId}
                isAdmin={isAdmin}
              />
            )}
            <ReviewManager
              taskName="driver_license"
              taskId={DRIVER_LICENSE_TASK_ID}
              referenceId={dlId}
              userId={userId}
              isAdmin={isAdmin}
              onReviewed={fetchTaskStatus}
            />
          </>
        )}
      </div>
    </>
  );
};

export default DriverLicenseForm;
